/**
 * Pokemon TCG Pocket — the digital-only game.
 *
 * Its cards live inside the Pocket app and are free to collect there. That is
 * the whole story, and it is a good one, so every surface says "Free" rather
 * than apologising for a missing number. An earlier version of this file read
 * these cards as a pricing failure ("never printed, so there is no market
 * price"), which turned a perfectly ordinary fact into an error message.
 *
 * What being free costs the card: no price anywhere, no wish list entry, and no
 * scan can match one, because a scan is a photograph of a printed card and
 * these were never printed. Browsing them is fine and wanted — they belong in
 * the All area with everything else.
 *
 * The era is a fact about the set, not the card, so the test is set-id
 * membership. The list is seeded with every TCG Pocket set that exists today,
 * which keeps the very first paint correct with no round trip, and refreshed
 * once per session from the catalogue so a set added later needs no release.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export const TCG_POCKET_ERA_ID = 'tcgp';

/** The product's name. For the places that are genuinely naming the game. */
export const TCG_POCKET_LABEL = 'TCG Pocket';

/**
 * The word that stands in the price slot. A price answers "what does this
 * cost", and for a Pocket card the honest answer is nothing, so that is what
 * the badge says. "TCG Pocket" used to sit here, which answered a question
 * nobody asked.
 */
export const TCG_POCKET_PRICE_LABEL = 'Free';

/** Why there is no number, said as the good news it actually is. */
export const TCG_POCKET_REASON = 'Digital cards from Pokemon TCG Pocket. They are free to collect in the app, so no price is attached to them.';
/** The same fact with only a few words of room. */
export const TCG_POCKET_SHORT = 'Free in the app';

/**
 * Where a trade happens for one of these. A different fact from the free one:
 * Trade Now is two people at a table swapping printed cards, and a card that
 * lives inside another app is collected and swapped inside that app instead.
 * Said as where it does happen rather than as something the card cannot do.
 */
export const TCG_POCKET_TRADE_REASON = 'These cards live inside the Pocket app, so collecting and swapping them happens there rather than across a table.';
/** The same fact for a chip, where only two or three words fit. */
export const TCG_POCKET_TRADE_SHORT = 'In-app only';

/**
 * The toast when every card in a selection is a TCG Pocket card, so nothing
 * was written. Says the rule rather than counting, which reads the same for
 * one card and for fifteen.
 */
export const TCG_POCKET_TRADE_NONE = 'TCG Pocket cards stay inside the Pocket app, so they cannot be marked tradable here.';

/**
 * The tail of the toast when a mixed selection was written and some cards were
 * left out. Names the number, then why, because a count that quietly shrinks is
 * how someone stops trusting the count.
 */
export function tcgPocketSkippedLine(n) {
  return n === 1
    ? '1 TCG Pocket card skipped. Those stay inside the Pocket app.'
    : `${n} TCG Pocket cards skipped. Those stay inside the Pocket app.`;
}

/**
 * Why a wish list will not take one. A wish list is a list of cards someone
 * wants to be given, bought or traded, and none of those three is how a Pocket
 * card is obtained: you open the app and it is free. So the refusal points at
 * the app rather than at a rule.
 */
export const TCG_POCKET_WISH_REASON = 'TCG Pocket cards are free in the Pocket app, so they do not go on a wish list. Open TCG Pocket and collect it there.';
/** The same for a button or a single line under one. */
export const TCG_POCKET_WISH_SHORT = 'Free in TCG Pocket';

/**
 * The warm aside on the card's own screen. This is the one place a person
 * actually stops and reads about a Pocket card, so it is the one place worth
 * spending two sentences on, and it should sound like a tip from a friend
 * rather than a wall the app put up.
 */
export const TCG_POCKET_FREE_TITLE = 'Free in TCG Pocket';
export const TCG_POCKET_FREE_BODY = 'This one only exists inside Pokemon TCG Pocket. Download the app and it is free to collect there, no price tag and nothing to hunt down.';

/**
 * What the scanner says when a photograph, or a correction search, lands on a
 * TCG Pocket card.
 *
 * Returning nothing looks like the scanner failed. Naming the card exactly is
 * the app showing it knows precisely what it is looking at, which is the whole
 * difference between "we could not read that" and "we read that perfectly, and
 * here is why it does not belong in a collection". The first line is the
 * finding, the second is what it means.
 */
export const TCG_POCKET_SCAN_LINE = 'That is a TCG Pocket card, not a physical trading card.';
export const TCG_POCKET_SCAN_HINT = 'Those are free inside the Pocket app and were never printed, so there is nothing to scan in. You can still browse it under All.';

/**
 * The same finding for the scanner's manual correction search, where the person
 * typed a name and every card that answered was a Pocket one.
 */
export const TCG_POCKET_SEARCH_NONE = 'Those are TCG Pocket cards, not physical trading cards. They are free in the Pocket app, so a scan cannot match one. Try the printed card instead.';

/**
 * Can this card go on a wish list? The single answer for every entry point, so
 * a screen added later inherits the rule instead of having to remember it.
 * Takes anything card-shaped, the same as isTcgPocketCard.
 */
export function canWishListCard(card) {
  return !isTcgPocketCard(card);
}

/** The same question for a bare catalogue id, where no card row is in hand. */
export function canWishListCardId(cardId) {
  return !isTcgPocketCardId(cardId);
}

// Every set in the tcgp era as of 09.11.2026. Seeded rather than awaited so the
// first grid paints with the right badge instead of a price-shaped gap.
const SEED_SET_IDS = [
  'A1', 'A1a', 'A2', 'A2a', 'A2b',
  'A3', 'A3a', 'A3b', 'A4', 'A4a',
  'B1', 'B1a', 'B2', 'B2a', 'P-A',
];

let knownSetIds = new Set(SEED_SET_IDS);
let refreshPromise = null;
let version = 0;
const listeners = new Set();

/** Is this catalogue set id a TCG Pocket set? */
export function isTcgPocketSet(setId) {
  return Boolean(setId) && knownSetIds.has(String(setId));
}

/**
 * The set id inside a catalogue card id.
 *
 * Catalogue ids are `${setId}-${localId}`, and a set id can carry its own dash
 * ("P-A-001"), so the split is on the LAST dash. Route ids arrive as
 * `tcg_${setId}_${localId}` instead and split on the last underscore.
 */
export function setIdFromCardId(cardId) {
  if (!cardId) return null;
  const raw = String(cardId);
  if (raw.startsWith('tcg_')) {
    const body = raw.slice(4);
    const cut = body.lastIndexOf('_');
    return cut > 0 ? body.slice(0, cut) : null;
  }
  const cut = raw.lastIndexOf('-');
  return cut > 0 ? raw.slice(0, cut) : null;
}

/** Is this catalogue card id (or route id) a TCG Pocket card? */
export function isTcgPocketCardId(cardId) {
  return isTcgPocketSet(setIdFromCardId(cardId));
}

/**
 * Is this card a TCG Pocket card? Takes anything card-shaped: a saved
 * collection row, a wish list row, or the browse-only shape CardDetail builds
 * from a `tcg_` route.
 */
export function isTcgPocketCard(card) {
  if (!card) return false;
  return isTcgPocketSet(
    card.tcg_set_id
    || card.set_id
    || setIdFromCardId(card.card_id || card.tcgdex_card_id || card.id),
  );
}

/**
 * Refresh the set list from the catalogue, once per session. Fails quietly —
 * the seed is already right for every set that ships today, so a failed read
 * costs nothing.
 */
export function ensureTcgPocketSets() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const { data, error } = await supabase
        .schema('digitalcards')
        .from('sets')
        .select('id')
        .eq('era_id', TCG_POCKET_ERA_ID);
      if (error || !data || data.length === 0) return;
      const next = new Set(SEED_SET_IDS);
      for (const row of data) if (row?.id) next.add(String(row.id));
      if (next.size === knownSetIds.size) return;
      knownSetIds = next;
      version += 1;
      listeners.forEach((fn) => fn(version));
    } catch {
      /* the seed stands */
    }
  })();
  return refreshPromise;
}

/**
 * Kick the refresh off and re-render when it actually adds a set. Screens that
 * render a price call this once so a newly ingested set is badged without a
 * release.
 */
export function useTcgPocketSets() {
  const [, setTick] = useState(version);
  useEffect(() => {
    const onChange = (v) => setTick(v);
    listeners.add(onChange);
    ensureTcgPocketSets();
    return () => { listeners.delete(onChange); };
  }, []);
}

/**
 * Drop every TCG Pocket card from a list of results.
 *
 * For the two surfaces that answer "which printed card is this": the scan
 * identifier and the scanner's manual correction search. Both are looking at a
 * physical card in someone's hand, and a Pocket card can never be the answer,
 * so it should never be offered as one. Photographing a phone screen showing a
 * Pocket card is the case that made this necessary.
 *
 * `pick` pulls whatever identifies the row, since every one of these lists has
 * its own shape: a catalogue row, a scan candidate, a fan result.
 */
export function withoutTcgPocket(rows, pick) {
  if (!Array.isArray(rows)) return rows;
  return rows.filter((row) => {
    const id = pick ? pick(row) : row;
    return !isTcgPocketSet(id) && !isTcgPocketCardId(id);
  });
}

export default {
  TCG_POCKET_ERA_ID,
  TCG_POCKET_LABEL,
  TCG_POCKET_PRICE_LABEL,
  TCG_POCKET_REASON,
  TCG_POCKET_SHORT,
  TCG_POCKET_TRADE_REASON,
  TCG_POCKET_TRADE_SHORT,
  TCG_POCKET_TRADE_NONE,
  TCG_POCKET_WISH_REASON,
  TCG_POCKET_WISH_SHORT,
  TCG_POCKET_FREE_TITLE,
  TCG_POCKET_FREE_BODY,
  TCG_POCKET_SCAN_LINE,
  TCG_POCKET_SCAN_HINT,
  TCG_POCKET_SEARCH_NONE,
  tcgPocketSkippedLine,
  canWishListCard,
  canWishListCardId,
  withoutTcgPocket,
  isTcgPocketSet,
  isTcgPocketCard,
  isTcgPocketCardId,
  setIdFromCardId,
  ensureTcgPocketSets,
  useTcgPocketSets,
};
